/**
 * Invoice detail (design D): the document as the customer sees it, its
 * balance, payments and activity, and every action the current status
 * allows (detailModel.availableActions). After any action the document is
 * fetched again, so the figures on screen are always the server's.
 *
 * `?action=pay` / `?action=void` open that dialog straight away (used by
 * the list's row menu), then the param is dropped so a reload doesn't.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import InvoiceRepository, { DocumentDetail, InvoiceDoc } from '../../../internals/repository/InvoiceRepository';
import { useInvoiceSettings, useSelectedShop } from '../useInvoicing';
import { date, dueHint, money } from '../format';
import {
  availableActions, balanceRows, errorText, fieldProblems, moneyRows, paidPercent,
} from '../detailModel';
import StatusChip from '../components/StatusChip';
import InvoicePreview from '../components/InvoicePreview';
import ActivityTimeline from '../components/ActivityTimeline';
import RecordPaymentModal from '../components/RecordPaymentModal';
import CreditNoteModal from '../components/CreditNoteModal';
import VoidModal, { Dialog, ErrorBanner } from '../components/VoidModal';
import ClaimPanel from '../components/ClaimPanel';
import ShareDialog from '../components/ShareDialog';

type Modal = null | 'pay' | 'credit' | 'void' | 'regen' | 'delete' | 'share' | { link: string };
interface Problem { text: string; fields: string[]; offerEdit?: boolean }

const capsCls = 'text-[11px] font-semibold uppercase tracking-[.08em] text-text3';

export default function InvoiceDetailView() {
  const { docId = '' } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const shop = useSelectedShop();
  const shopId: string = shop?.shopId ?? '';
  const { settings } = useInvoiceSettings(shopId || undefined);

  const [detail, setDetail] = useState<DocumentDetail | null>(null);
  const [loadError, setLoadError] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<Modal>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const flash = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast((t) => (t === text ? null : t)), 3000);
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!shopId || !docId) return;
    if (!quiet) setLoading(true);
    try {
      const d = await InvoiceRepository.get(shopId, docId);
      setDetail(d);
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
      if (!quiet) setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [shopId, docId]);

  useEffect(() => { load(); }, [load]);

  const doc = detail?.document;
  const actions = useMemo(() => (doc ? availableActions(doc) : new Set()), [doc]);

  // Deep links from the list: open the dialog once the document is here.
  useEffect(() => {
    const action = search.get('action');
    if (!action || !doc) return;
    if (action === 'pay' && actions.has('recordPayment')) setModal('pay');
    if (action === 'void' && actions.has('void')) setModal('void');
    const next = new URLSearchParams(search);
    next.delete('action');
    setSearch(next, { replace: true });
  }, [search, doc, actions, setSearch]);

  // A4 preview scaled to the width of its column.
  const canvasRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;
    const fit = () => setScale(Math.min(1, Math.max(0.3, (el.clientWidth - 48) / 595)));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [doc?.docId]);

  // More menu: Escape or a click outside closes it.
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: MouseEvent) => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  const run = async (name: string, fn: () => Promise<void>, overrides: Record<string, string> = {}, offerEdit = false) => {
    setMenuOpen(false);
    setBusy(name);
    setProblem(null);
    try {
      await fn();
    } catch (e: any) {
      setProblem({ text: errorText(e, overrides), fields: fieldProblems(e?.fields), offerEdit });
    } finally {
      setBusy(null);
    }
  };

  const issue = () => run('issue', async () => {
    const res = await InvoiceRepository.issue(shopId, docId);
    await load(true);
    flash(`Issued as ${res.document.number}`);
  }, {}, true);

  const removeDraft = () => run('delete', async () => {
    setModal(null);
    await InvoiceRepository.remove(shopId, docId);
    navigate('/invoices');
  });

  const duplicate = () => run('duplicate', async () => {
    const res = await InvoiceRepository.duplicate(shopId, docId);
    navigate(`/invoices/${encodeURIComponent(res.document.docId)}/edit`);
  });

  const pdf = (size: 'A4' | '80mm', copy: 'ORIGINAL' | 'DUPLICATE') => {
    // Open the tab inside the click so pop-up blockers allow it, then point it at the signed URL.
    const win = window.open('', '_blank');
    return run('pdf', async () => {
      try {
        const { url } = await InvoiceRepository.pdfUrl(shopId, docId, size, copy);
        if (win) { win.opener = null; win.location.href = url; } else window.open(url, '_blank', 'noopener');
      } catch (e) {
        win?.close();
        throw e;
      }
    }, { invalid_state: 'A PDF is made when the document is issued.' });
  };

  const regenerate = () => run('regen', async () => {
    setModal(null);
    const res = await InvoiceRepository.regenerateLink(shopId, docId);
    setModal({ link: res.shareUrl });
    await load(true);
  });

  const copyLink = async (url?: string) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      flash('Link copied');
    } catch {
      setProblem({ text: `Copy did not work. The link is ${url}`, fields: [] });
    }
  };

  const toggleReminders = (paused: boolean) => run('reminders', async () => {
    await InvoiceRepository.setRemindersPaused(shopId, docId, paused);
    await load(true);
    flash(paused ? 'Reminders paused' : 'Reminders resumed');
  });

  const afterModal = async (text: string) => {
    setModal(null);
    await load(true);
    flash(text);
  };

  // ── States ────────────────────────────────────────────────────────────────
  const shell = 'h-screen flex flex-col w-full p-5 md:px-8 md:py-7 overflow-auto [&>*]:shrink-0 gap-5';

  if (!shopId) {
    return <div className={shell}><p className="text-text2">Select a shop to see its invoices.</p></div>;
  }
  if (loading && !detail) {
    return (
      <div className={shell} aria-busy="true" aria-label="Loading invoice">
        <div className="h-4 w-28 animate-pulse rounded bg-surface3" />
        <div className="h-8 w-64 animate-pulse rounded bg-surface3" />
        <div className="grid grid-cols-1 gap-5 min-[1180px]:grid-cols-[minmax(0,1fr)_380px]">
          <div className="h-[600px] animate-pulse rounded-2xl bg-surface3" />
          <div className="flex flex-col gap-3.5">
            {[140, 120, 220].map((h) => <div key={h} className="animate-pulse rounded-2xl bg-surface3" style={{ height: h }} />)}
          </div>
        </div>
      </div>
    );
  }
  if (!detail || !doc) {
    const notFound = loadError?.code === 'not_found' || loadError?.status === 404;
    return (
      <div className={shell}>
        <Link to="/invoices" className="text-[13px] text-text3 hover:text-brand">← All invoices</Link>
        <div className="nl-card flex flex-col items-center gap-3 px-6 py-16 text-center">
          <h1 className="m-0 text-[18px] font-semibold text-text1">{notFound ? 'Invoice not found' : 'This invoice could not load'}</h1>
          <p className="m-0 max-w-md text-[14px] text-text2">
            {notFound ? 'This invoice does not exist or belongs to another shop.' : errorText(loadError)}
          </p>
          {notFound
            ? <Link to="/invoices" className="nl-btn nl-btn--secondary">Back to invoices</Link>
            : <button type="button" className="nl-btn nl-btn--secondary" onClick={() => load()}>Try again</button>}
        </div>
      </div>
    );
  }

  // ── Document ──────────────────────────────────────────────────────────────
  const cur = doc.currency;
  const isQuote = doc.docType === 'QUOTATION';
  const isInvoice = doc.docType === 'INVOICE';
  const isDraft = doc.status === 'DRAFT';
  const isVoid = doc.status === 'VOID';
  const overdue = doc.displayStatus === 'OVERDUE';
  const hint = isInvoice && !isDraft && !isVoid && doc.displayStatus !== 'PAID' ? dueHint(doc.dueDate) : null;
  const cust = doc.customer || ({} as InvoiceDoc['customer']);
  const custMain = cust.companyName || cust.name || 'No customer';
  // Names in the body font; only the phone number is mono (design type rules).
  const custName = cust.companyName ? cust.name : '';
  const custContact = [cust.phoneE164, cust.email].filter(Boolean).join(' · ');
  const rows = moneyRows(detail);
  const pendingClaims = (detail.payments || []).filter((p) => p.status === 'PENDING_APPROVAL');
  const pct = paidPercent(doc);
  const has = (a: string) => actions.has(a as any);
  const branding = {
    accentColor: settings?.branding?.accentColor,
    footerText: settings?.branding?.footerText,
    bankAccounts: settings?.bankAccounts,
    shopTin: settings?.shopTin,
  };
  const voidedOn = doc.voidedAt ? date(new Date(doc.voidedAt + 330 * 60000).toISOString()) : null;

  const menuItem = 'flex w-full items-center rounded-lg border-0 bg-transparent px-2.5 py-2 text-left text-[14px] font-medium text-text1 hover:bg-surface2 disabled:opacity-50';
  const showMenu = has('pdf') || has('duplicate') || has('creditNote') || has('regenerateLink') || has('void');

  return (
    <div className={shell}>
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-5">
        <div className="flex min-w-0 flex-col gap-2">
          <Link to="/invoices" className="self-start text-[13px] text-text3 hover:text-brand">← All invoices</Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="m-0 font-mono text-[26px] font-semibold tracking-[-0.02em] text-text1">
              {doc.number ?? (isQuote ? 'Draft quotation' : 'Draft')}
            </h1>
            <StatusChip status={doc.displayStatus} />
            {doc.isTaxInvoice && (
              <span className="inline-flex h-[22px] items-center rounded-md border border-borderc2 px-2 text-[12px] font-semibold text-text2">Tax invoice</span>
            )}
            {doc.revision > 1 && <span className="text-[12px] text-text3">Revision {doc.revision}</span>}
          </div>
          <span className="text-[14px] text-text2">
            <span className="font-semibold text-text1">{custMain}</span>
            {custName && <> · <span>{custName}</span></>}
            {custContact && <> · <span className="nl-num">{custContact}</span></>}
          </span>
          {detail.related.parent && (
            <span className="text-[13px] text-text3">
              From <Link className="nl-num text-brand" to={`/invoices/${encodeURIComponent(detail.related.parent.docId)}`}>{detail.related.parent.number ?? 'draft'}</Link>
            </span>
          )}
        </div>

        <div className="flex flex-col items-end gap-3">
          <dl className="m-0 flex flex-wrap justify-end gap-6">
            <div className="flex flex-col items-end">
              <dt className={capsCls}>Total</dt>
              <dd className="m-0 nl-num text-[17px] font-semibold text-text1">{money(doc.totalMinor, cur)}</dd>
            </div>
            {isInvoice && !isDraft && (
              <div className="flex flex-col items-end">
                <dt className={capsCls}>Balance due</dt>
                <dd
                  className="m-0 nl-num text-[17px] font-semibold"
                  style={{ color: overdue ? 'var(--negFg)' : doc.displayStatus === 'PAID' ? 'var(--posFg)' : 'var(--text)' }}
                >
                  {isVoid ? '—' : money(Math.max(0, doc.balanceMinor), cur)}
                </dd>
              </div>
            )}
            <div className="flex flex-col items-end">
              <dt className={capsCls}>{isQuote ? 'Valid until' : 'Due date'}</dt>
              <dd className="m-0 text-[17px] font-semibold" style={{ color: overdue ? 'var(--negFg)' : 'var(--text)' }}>
                {date(isQuote ? doc.validUntil : doc.dueDate)}
              </dd>
              {hint && (
                <span className="text-[12px] font-semibold" style={{ color: hint.overdue ? 'var(--negFg)' : 'var(--text3)' }}>{hint.text}</span>
              )}
            </div>
          </dl>

          <div className="flex flex-wrap justify-end gap-2">
            {!isDraft && (
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal('share')}>Share</button>
            )}
            {has('copyLink') && (
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => copyLink(doc.shareUrl)}>Copy link</button>
            )}
            {isDraft && (
              <>
                <button type="button" className="nl-btn nl-btn--danger-outline" disabled={!!busy} onClick={() => setModal('delete')}>Delete draft</button>
                <Link to={`/invoices/${encodeURIComponent(doc.docId)}/edit`} className="nl-btn nl-btn--secondary">Edit</Link>
                <button type="button" className="nl-btn nl-btn--primary" disabled={!!busy} onClick={issue}>
                  {busy === 'issue' ? 'Issuing…' : isQuote ? 'Issue quotation' : 'Issue invoice'}
                </button>
              </>
            )}
            {!isDraft && has('edit') && (
              <Link
                to={`/invoices/${encodeURIComponent(doc.docId)}/edit`}
                className="nl-btn nl-btn--secondary"
                title="Saving creates a new revision with the same number and link"
              >
                Edit
              </Link>
            )}
            {has('recordPayment') && (
              <button type="button" className="nl-btn nl-btn--primary" onClick={() => setModal('pay')}>Record payment</button>
            )}
            {!isDraft && showMenu && (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  className="nl-btn nl-btn--secondary"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label="More actions"
                  disabled={!!busy}
                  onClick={() => setMenuOpen((o) => !o)}
                >
                  {busy && busy !== 'issue' ? 'Working…' : 'More ▾'}
                </button>
                {menuOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-[calc(100%+6px)] z-30 flex w-[240px] flex-col rounded-xl border border-borderc bg-surface p-1.5"
                    style={{ boxShadow: 'var(--shadowLg)' }}
                  >
                    {has('pdf') && (
                      <>
                        <span className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-[.07em] text-text3">Download PDF</span>
                        <button role="menuitem" type="button" className={menuItem} onClick={() => pdf('A4', 'ORIGINAL')}>Original · A4</button>
                        <button role="menuitem" type="button" className={menuItem} onClick={() => pdf('A4', 'DUPLICATE')}>Duplicate · A4</button>
                        <button role="menuitem" type="button" className={menuItem} onClick={() => pdf('80mm', 'ORIGINAL')}>80 mm thermal</button>
                        <div className="my-1 h-px bg-borderc" />
                      </>
                    )}
                    {has('duplicate') && <button role="menuitem" type="button" className={menuItem} onClick={duplicate}>Duplicate</button>}
                    {has('creditNote') && (
                      <button role="menuitem" type="button" className={menuItem} onClick={() => { setMenuOpen(false); setModal('credit'); }}>Credit note</button>
                    )}
                    {has('regenerateLink') && (
                      <button role="menuitem" type="button" className={menuItem} onClick={() => { setMenuOpen(false); setModal('regen'); }}>Regenerate link</button>
                    )}
                    {has('void') && (
                      <button
                        role="menuitem"
                        type="button"
                        className={menuItem}
                        style={{ color: 'var(--negFg)' }}
                        onClick={() => { setMenuOpen(false); setModal('void'); }}
                      >
                        Void invoice
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {problem && (
        <ErrorBanner text={problem.text}>
          {problem.fields.length > 0 && (
            <ul className="mb-0 mt-1.5 list-disc pl-5">{problem.fields.map((f) => <li key={f}>{f}</li>)}</ul>
          )}
          <div className="mt-2 flex gap-2">
            {problem.offerEdit && (
              <Link to={`/invoices/${encodeURIComponent(doc.docId)}/edit`} className="nl-btn nl-btn--xs nl-btn--secondary">Edit</Link>
            )}
            <button type="button" className="nl-btn nl-btn--xs nl-btn--ghost" onClick={() => setProblem(null)}>Dismiss</button>
          </div>
        </ErrorBanner>
      )}

      {isVoid && (
        <div className="flex gap-3 rounded-xl border border-borderc2 px-4 py-3.5" style={{ background: 'var(--neutralSoft)' }}>
          <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-surface text-text2">⊘</span>
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-text1">This invoice is void</span>
            <span className="text-[13px] text-text2">
              {[doc.voidReason || 'Voided', voidedOn && `Voided on ${voidedOn}`].filter(Boolean).join(' · ')}
              {' · '}The customer link shows a void notice and reminders have stopped.
            </span>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="grid grid-cols-1 items-start gap-5 min-[1180px]:grid-cols-[minmax(0,1fr)_380px]">
        <div ref={canvasRef} className="flex justify-center overflow-hidden rounded-2xl p-6" style={{ background: 'var(--paperCanvas)' }}>
          <InvoicePreview doc={doc as any} branding={branding} shop={shop} scale={scale} />
        </div>

        <div className="flex min-w-0 flex-col gap-3.5">
          {isInvoice && !isDraft && pendingClaims.map((k) => (
            <ClaimPanel
              key={k.paymentId}
              shopId={shopId}
              claim={k}
              balanceMinor={Math.max(0, doc.balanceMinor)}
              docNumber={doc.number}
              currency={cur}
              onDecided={async (text) => { await load(true); if (text) flash(text); }}
            />
          ))}

          {isInvoice && !isDraft && (
            <section className="nl-card flex flex-col gap-2 p-5" aria-label="Balance">
              <span className={capsCls}>Balance</span>
              {balanceRows(doc).map((r) => (
                <div
                  key={r.label}
                  className="flex justify-between gap-2.5 text-[14px]"
                  style={{
                    color: r.tone === 'pos' ? 'var(--posFg)' : r.tone === 'strong' ? 'var(--text)' : 'var(--text2)',
                    fontWeight: r.tone === 'strong' ? 700 : 400,
                  }}
                >
                  <span>{r.label}</span><span className="nl-num tabular-nums">{r.value}</span>
                </div>
              ))}
              <div
                className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface2"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                aria-label="Collected"
              >
                <span className="block h-full" style={{ width: `${pct}%`, background: 'var(--pos)' }} />
              </div>
              <span className="text-[12px] text-text3">{isVoid ? 'Void invoices have no balance.' : `${pct}% collected`}</span>
            </section>
          )}

          {isInvoice && !isDraft && (
            <section className="nl-card overflow-hidden" aria-labelledby="inv-payments">
              <div className="flex items-center justify-between px-[18px] py-3.5">
                <h2 id="inv-payments" className="m-0 text-[14px] font-semibold text-text1">Payments</h2>
                {has('recordPayment') && (
                  <button type="button" className="border-0 bg-transparent text-[13px] font-semibold text-brand" onClick={() => setModal('pay')}>+ Record</button>
                )}
              </div>
              {rows.length === 0 && (
                <p className="m-0 border-t border-borderc px-[18px] py-3.5 text-[13px] text-text3">No payments recorded yet.</p>
              )}
              {rows.map((p) => (
                <div key={p.key} className="grid gap-x-2.5 gap-y-0.5 border-t border-borderc px-[18px] py-2.5" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                  <span
                    className={`font-semibold ${p.rejected ? 'text-text3' : 'text-text1'}`}
                    style={p.kind === 'credit' ? { color: 'var(--infoFg)' } : undefined}
                  >
                    {p.title}{p.refunded ? ' · refunded' : ''}
                  </span>
                  <span
                    className={`nl-num font-semibold tabular-nums ${p.rejected ? 'line-through' : ''}`}
                    style={{ color: p.kind === 'credit' ? 'var(--infoFg)' : p.refunded || p.rejected ? 'var(--text3)' : 'var(--posFg)' }}
                  >
                    {money(p.amountMinor, cur)}
                  </span>
                  <span className="min-w-0 truncate text-[12px] text-text3">
                    {p.rejected
                      ? `Rejected · ${p.rejected}`
                      : [p.paidOn ? date(p.paidOn) : p.at ? date(new Date(p.at + 330 * 60000).toISOString()) : null, p.reference].filter(Boolean).join(' · ')}
                  </span>
                  <span className="nl-num text-right text-[12px] text-text3">{p.kind === 'payment' ? p.number ?? '' : ''}</span>
                </div>
              ))}
            </section>
          )}

          {has('reminders') && doc.reminders && (
            <section className="nl-card flex items-center justify-between gap-3 p-[18px]" aria-label="Reminders">
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-text1">{doc.reminders.paused ? 'Reminders paused' : 'Next reminder'}</span>
                <span className="text-[13px] text-text2">Reminders start in a later release.</span>
              </div>
              <button
                type="button"
                className="nl-btn nl-btn--sm nl-btn--secondary"
                disabled={!!busy}
                onClick={() => toggleReminders(!doc.reminders.paused)}
              >
                {busy === 'reminders' ? '…' : doc.reminders.paused ? 'Resume' : 'Pause'}
              </button>
            </section>
          )}

          <ActivityTimeline events={detail.events} currency={cur} />
        </div>
      </div>

      {/* Dialogs */}
      {modal === 'pay' && (
        <RecordPaymentModal
          shopId={shopId}
          doc={doc}
          onClose={() => setModal(null)}
          onDone={(r) => afterModal(`${money(r.payment.amountMinor, cur)} recorded${r.receipt?.number ? ` · receipt ${r.receipt.number}` : ''}`)}
        />
      )}
      {modal === 'credit' && (
        <CreditNoteModal
          shopId={shopId}
          doc={doc}
          onClose={() => setModal(null)}
          onDone={(r) => afterModal(r.creditNote.number ? `Credit note ${r.creditNote.number} issued` : 'Credit note issued')}
        />
      )}
      {modal === 'void' && (
        <VoidModal shopId={shopId} doc={doc} onClose={() => setModal(null)} onDone={(d) => afterModal(`${d.number ?? 'Invoice'} voided`)} />
      )}
      <ShareDialog
        open={modal === 'share'}
        onClose={() => setModal(null)}
        shopId={shopId}
        docId={doc.docId}
        document={doc}
        onShared={() => load(true)}
      />
      {modal === 'regen' && (
        <Dialog
          title="Regenerate the share link?"
          sub="The old link stops working at once. Anyone who opens it sees a not-found page. Send the new link to the customer yourself."
          onClose={() => setModal(null)}
          busy={busy === 'regen'}
          footer={(
            <>
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal(null)} disabled={busy === 'regen'}>Cancel</button>
              <button type="button" className="nl-btn nl-btn--primary" onClick={regenerate} disabled={busy === 'regen'}>
                {busy === 'regen' ? 'Regenerating…' : 'Regenerate link'}
              </button>
            </>
          )}
        />
      )}
      {modal && typeof modal === 'object' && (
        <Dialog
          title="New link created"
          sub="The old link is revoked."
          onClose={() => setModal(null)}
          footer={(
            <>
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal(null)}>Done</button>
              <button type="button" className="nl-btn nl-btn--primary" onClick={() => copyLink(modal.link)}>Copy link</button>
            </>
          )}
        >
          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-text2">
            Share link
            <input className="nl-input nl-num" readOnly value={modal.link} onFocus={(e) => e.target.select()} />
          </label>
        </Dialog>
      )}
      {modal === 'delete' && (
        <Dialog
          title="Delete this draft?"
          sub="The draft is removed. Drafts never hold a number, so nothing is skipped in your numbering."
          onClose={() => setModal(null)}
          busy={busy === 'delete'}
          footer={(
            <>
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal(null)} disabled={busy === 'delete'}>Cancel</button>
              <button type="button" className="nl-btn nl-btn--danger" onClick={removeDraft} disabled={busy === 'delete'}>
                {busy === 'delete' ? 'Deleting…' : 'Delete draft'}
              </button>
            </>
          )}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-[1100] flex -translate-x-1/2 items-center gap-2 rounded-[10px] px-4 py-2.5 text-[14px] font-medium text-white"
          style={{ background: '#131318', boxShadow: 'var(--shadowLg)' }}
        >
          <span aria-hidden style={{ color: '#45D39F' }}>✓</span>{toast}
        </div>
      )}
    </div>
  );
}
